import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CianChatButtonWithAnimationContainer } from './CianChatButtonWithAnimationContainer';
import { onEntryAutorisationPopupClick, onEntryAutorisationPopupOpen, onEntryPointClick } from './analytics/analytics';
import { useConsultantCianChat } from './hooks/useConsultantCianChat';
import {
  closeConsultantCianChat,
  closeErrorMessage,
  openAutorisationMotivationConsultantCianChat,
} from '../../actions/consultantCianChat';
import { AutorisationMotivationPopup } from '../../components/AutorisationMotivationPopup';
import { ConsultantCianChatButton, ConsultantCianChatButtonContainer } from '../../components/ConsultantCianChatButton';
import { ConsultantCianChatPopup } from '../../components/ConsultantCianChatPopup';
import {
  isOpenSelector,
  isAutorisationPopupOpenSelector,
  openedChatIdSelector,
  customLabelSelector,
  isErrorSelector,
  isErrorOpenedSelector,
  isLoadingSelector,
} from '../../selectors/consultantCianChat';
import { getUser } from '../../selectors/user';
import { ConsultantCianChatOnboarding } from '../ConsultantCianChatOnboarding';
import { IAppState } from 'shared/common/state';
import { ConsultantCianChatErrorPopup } from 'shared/components/ConsultantCianChatErrorPopup';
import { IJsonQuery } from 'shared/json_query';
import { getCurrentRegionId } from 'shared/selectors/getCurrentRegionId';
import { useApplicationContext } from 'shared/utils/applicationContext';

export const ConsultantCianChat = () => {
  const context = useApplicationContext();
  const isButtonAnimationVisible = context.config.get('frontend-serp.Newbuilding.CianKpnChatAnimation.Enabled');
  const dispatch = useDispatch();
  const isOpened = useSelector(isOpenSelector);
  const openedChatId = useSelector(openedChatIdSelector);
  const isAutorisationPopupOpen = useSelector(isAutorisationPopupOpenSelector);
  const jsonQuery = useSelector<IAppState, IJsonQuery>(state => state.results.jsonQuery);
  const customLabel = useSelector(customLabelSelector);
  const user = useSelector(getUser);
  const isError = useSelector(isErrorSelector);
  const isErrorOpened = useSelector(isErrorOpenedSelector);
  const isLoading = useSelector(isLoadingSelector);
  const regionId = useSelector(getCurrentRegionId);
  const frameUrl = `/dialogs/?hostType=frame&type=bot&pageType=Listing&chatId=${openedChatId}&region=${regionId}`;
  const { openChat } = useConsultantCianChat();

  const onClick = React.useCallback(() => {
    if (!isLoading) {
      if (!user.isAuthenticated) {
        dispatch(openAutorisationMotivationConsultantCianChat());
        onEntryAutorisationPopupOpen({ jsonQuery, label: customLabel, regionId });
      } else {
        openChat();
      }
      onEntryPointClick({ jsonQuery, regionId });
    }
  }, [isLoading, user.isAuthenticated, jsonQuery, dispatch, customLabel, openChat, regionId]);

  const onLogInClick = React.useCallback(() => {
    openChat();
    onEntryAutorisationPopupClick({ jsonQuery, label: customLabel, regionId });
  }, [customLabel, jsonQuery, openChat, regionId]);

  const onClose = React.useCallback(() => {
    dispatch(closeConsultantCianChat());
  }, [dispatch]);

  const onLogInClose = React.useCallback(() => {
    dispatch(closeConsultantCianChat());
  }, [dispatch]);

  const handleErrorClose = React.useCallback(() => {
    dispatch(closeErrorMessage());
  }, [dispatch]);

  return (
    <>
      {!isError && (
        <ConsultantCianChatButtonContainer>
          {isButtonAnimationVisible ? (
            <CianChatButtonWithAnimationContainer onClick={onClick} />
          ) : (
            <ConsultantCianChatOnboarding>
              <ConsultantCianChatButton isLoading={isLoading} onClick={onClick} />
            </ConsultantCianChatOnboarding>
          )}
        </ConsultantCianChatButtonContainer>
      )}

      <AutorisationMotivationPopup
        isAutorisationPopupOpened={isAutorisationPopupOpen}
        onClick={onLogInClick}
        onClose={onLogInClose}
      />
      {isOpened && <ConsultantCianChatPopup onClose={onClose} url={frameUrl} />}
      {isErrorOpened && <ConsultantCianChatErrorPopup open={isErrorOpened} onClose={handleErrorClose} />}
    </>
  );
};
